import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import {
  AS_AT, AUM_TRAIL, BEYOND_THREE_YEARS, BOOKS, BRIDGE_RECONCILES, DISTRIBUTABLE,
  LADDER_BANDED, LADDER_RECONCILES, LADDER_UNCLASSIFIED, LIQUIDITY_LADDER, MONTH_LABEL,
  MONTH_PCT, MONTH_PNL, NET_FLOWS, OPENING_AUM, POLICY_LIMITS, RUNOFF_EXPECTED,
  RUNOFF_REALISED, RUNOFF_SCHEDULE, SAME_DAY, TOTAL_AUM, TOTAL_PNL, WITHIN_WEEK, WITHIN_YEAR,
  YTD_ON_OPENING, bandLabel, bookTotals, findBook, findLine, hasPerformance, holdingRows,
  limitStatus, lineAlpha, weightOf,
} from './dashboardData';
import {
  Collapse, Disclosure, Figure, Headline, Illustrative, Kpi, KpiGrid, PairedBars, Panel,
  PerfTriple, Sheet, Sparkline, TONE, TapRow, Tabs, fmtM, fmtPct, fmtSigned, fmtSignedM,
  fmtSignedPct, signClass,
} from './DashboardUI';

// The investment dashboard. Five tabs, built for a SHAREHOLDER ON A PHONE.
//
// WHAT CHANGED AND WHY. This screen used to open with four equally-weighted KPI tiles above a
// seven-column table of sub-portfolio benchmark deltas. At 375px that is a wall of
// near-identical cards: nothing is bigger than anything else, so nothing is read first, and
// the first real content a shareholder meets is a per-sleeve alpha column he did not ask for.
//
// THE OVERVIEW IS NOW FOUR BLOCKS AND NOTHING ELSE, in the order an owner asks: what am I
// worth and why did it move; how did each book do against what it is measured on; how much
// can I get to in cash; what is at risk. Each block has one dominant figure.
//
// THE DETAIL IS NOT DELETED, IT IS MOVED TO THE BOOKS TAB. The eleven sub-portfolios and
// their returns, benchmarks and alphas were on the Overview inside a collapsed disclosure;
// they now live in Books, which is where a reader goes for exactly that, and the Overview
// carries the same comparison at BOOK level — three rows instead of eleven. Tapping a book,
// from either place, opens its portfolios with the same figures. Nothing was dropped in the
// move; if you take a figure out of Books, put it back on the Overview first.
//
// DRILL-DOWNS. Any portfolio line, from the Overview or the Books tab, opens its holdings in
// a bottom sheet: security, cost, current value, P&L both ways, weight, and how quickly it
// turns into cash. Reached from the Overview it opens the book first and the line second, so
// the sheet retraces the tap that opened it; reached from a row that already names the line
// it opens straight on the holdings.
//
// PERFORMANCE AND FLOWS ARE NEVER ADDED TOGETHER. The value line plots portfolio value, says
// so on itself, and is followed by a bridge — opening, performance, money in and out,
// closing. The change figures beside the headline are labelled "performance" because with
// flows in the period "this year" does not distinguish what the portfolio earned from what
// was paid into it. See the value-bridge block in dashboardData.js.
//
// THE FIFTH TAB IS "COMPLIANCE", NOT "POLICIES": the Policy Pack is a separate area of this
// portal and a tab of that name reads as a link to it. See the section comment there.
//
// Every figure is illustrative and the banner says so on every tab. See dashboardData.js.

const runoff = BOOKS.find((b) => b.key === 'runoff');
const fmtDate = (s) => new Date(s).toLocaleDateString('en-GB',
  { day: 'numeric', month: 'long', year: 'numeric' });
const pctOfBook = (v) => fmtPct(weightOf(v));

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'books', label: 'Books' },
  { key: 'liquidity', label: 'Liquidity' },
  { key: 'runoff', label: 'Run-off' },
  { key: 'compliance', label: 'Compliance' },
];

export default function InvestmentDashboard() {
  const [tab, setTab] = useState('overview');

  // The drill-down is a STACK, not a single selection: a reader who arrives at holdings by
  // way of a book must be able to step back to that book rather than out to the page. A
  // single `selected` would make Back and Close the same control and lose the path.
  const [stack, setStack] = useState([]);
  const openBook = useCallback((bookKey) => setStack([{ bookKey }]), []);
  const pushLine = useCallback((bookKey, lineName) =>
    setStack((s) => [...s, { bookKey, lineName }]), []);
  const openLine = useCallback((bookKey, lineName) => setStack([{ bookKey, lineName }]), []);
  const back = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const close = useCallback(() => setStack([]), []);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 text-[11px]">
        <Link to="/" className="flex shrink-0 items-center gap-1 text-gray-400 transition-colors hover:text-brand-gold dark:text-gray-500">
          <MdArrowBack size={14} />
          All areas
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h1 className="font-serif text-2xl text-gray-900 dark:text-gray-100">Investment Dashboard</h1>
        <span className="text-[11px] text-gray-400">As at {fmtDate(AS_AT)}</span>
      </div>

      {/* On every tab, not just the first — a reader can land on any of them. */}
      <Illustrative />

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'overview' && <Overview onBook={openBook} />}
      {tab === 'books' && <Books onLine={openLine} />}
      {tab === 'liquidity' && <Liquidity />}
      {tab === 'runoff' && <Runoff onLine={openLine} />}
      {tab === 'compliance' && <Compliance />}

      <Drilldown stack={stack} onLine={pushLine} onBack={back} onClose={close} />
    </div>
  );
}

// --- drill-down ---------------------------------------------------------------------------

/** One sheet, two depths: a book's portfolios, and a portfolio's holdings. */
function Drilldown({ stack, onLine, onBack, onClose }) {
  const top = stack[stack.length - 1] || null;
  const book = top ? findBook(top.bookKey) : null;
  const line = top?.lineName ? findLine(top.bookKey, top.lineName) : null;
  const t = book ? bookTotals(book) : null;

  return (
    <Sheet
      open={!!book}
      onClose={onClose}
      onBack={stack.length > 1 ? onBack : undefined}
      eyebrow={line ? book?.name : undefined}
      title={line ? line.name : book?.name || ''}
      meta={line
        ? `${line.holdings.length} holdings · AED ${fmtM(line.aum)} m · ${pctOfBook(line.aum)} of the whole book`
        : book && `${book.note} · AED ${fmtM(t.aum)} m · ${pctOfBook(t.aum)} of the whole book`}
    >
      {line
        ? <Holdings line={line} tone={book.tone} />
        : book && <BookLines book={book} total={t} onLine={onLine} />}
    </Sheet>
  );
}

/** A book's portfolios, one tap from the Overview.
 *
 * This is the "push Core Investments and it shows the breakdown" surface, so it carries the
 * same per-line return, benchmark and alpha the Books tab does — a reader who taps a book
 * from the Overview must not get a lesser view of it than one who navigates to the tab.
 */
function BookLines({ book, total, onLine }) {
  return (
    <div>
      <p className="mb-2 text-[10.5px] leading-relaxed text-gray-400">{book.basis}</p>
      {book.lines.map((l) => (
        <TapRow key={l.name} label={`${l.name} — show holdings`}
                onClick={() => onLine(book.key, l.name)}
                className="border-t border-gray-100 first:border-t-0 dark:border-gray-800/60">
          <span className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[13px] text-gray-900 dark:text-gray-100">{l.name}</span>
            <span className="shrink-0 font-serif text-[15px] tabular-nums text-gray-900 dark:text-gray-100">
              {fmtM(l.aum)}
            </span>
          </span>
          <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <span className="block h-full rounded-full"
                  style={{ width: `${Math.max(1, (l.aum / total.aum) * 100)}%`,
                           background: TONE[book.tone].bar }} />
          </span>
          <span className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 text-[10.5px] text-gray-400">
            <span className="tabular-nums">{l.holdings.length} holdings</span>
            <span className={`tabular-nums ${signClass(l.pnl)}`}>{fmtSignedM(l.pnl)} this year</span>
          </span>
          <PerfTriple ret={l.ret} bmk={l.bmk} alpha={lineAlpha(l)} className="mt-2" />
        </TapRow>
      ))}
      <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-gray-200 pt-2.5 text-[12.5px] font-semibold dark:border-gray-700">
        <span>Total</span>
        <span className="flex items-baseline gap-3 tabular-nums">
          <span>{fmtM(total.aum)}</span>
          <span className={signClass(total.pnl)}>{fmtSignedM(total.pnl)}</span>
        </span>
      </div>
    </div>
  );
}

/** The holdings of one portfolio.
 *
 * Totals are summed from the rows on screen rather than read off the line, so the footer
 * cannot disagree with the list above it even if a row were ever filtered out of the view.
 *
 * Cards on a phone, a table from `sm`. Five columns need about 560px; at 375 the security
 * name would scroll out of sight before its P&L arrived, and a P&L you cannot see beside the
 * name it belongs to is not worth showing.
 */
function Holdings({ line, tone }) {
  const rows = holdingRows(line);
  const value = rows.reduce((s, h) => s + h.value, 0);
  const invested = rows.reduce((s, h) => s + h.invested, 0);
  const pnl = value - invested;
  const pnlPct = invested ? (pnl / invested) * 100 : null;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-5 gap-y-2 rounded-xl bg-gray-50 px-3.5 py-3 dark:bg-gray-800/40">
        <Figure label="Invested" value={fmtM(invested)} />
        <Figure label="Current value" value={fmtM(value)} />
        <Figure label="Gain / loss" value={fmtSignedM(pnl)} sign={pnl}
                sub={`${fmtSignedPct(pnlPct)} on cost`} />
      </div>

      <div className="flex flex-col gap-2 sm:hidden">
        {rows.map((h) => (
          <div key={h.name} className="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
            <div className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 text-[13px] leading-snug text-gray-900 dark:text-gray-100">{h.name}</span>
              <span className={`shrink-0 font-serif text-[16px] tabular-nums ${TONE[tone].text}`}>
                {fmtM(h.value)}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <Cell label="Invested" value={fmtM(h.invested)} />
              <Cell label="Gain / loss" value={fmtSignedM(h.pnl)} sign={h.pnl}
                    sub={fmtSignedPct(h.pnlPct)} />
              <Cell label="Weight" value={fmtPct(h.weight)} sub={bandLabel(h.liq)} />
            </div>
          </div>
        ))}
      </div>

      <table className="hidden w-full text-sm sm:table">
        <thead>
          <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:border-gray-800 dark:text-gray-400">
            <th className="px-2 py-2">Security</th>
            <th className="px-2 py-2 text-right">Invested</th>
            <th className="px-2 py-2 text-right">Value</th>
            <th className="px-2 py-2 text-right">Gain / loss</th>
            <th className="px-2 py-2 text-right">Weight</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.name} className="border-b border-gray-100 dark:border-gray-800/60">
              <td className="px-2 py-2 text-gray-800 dark:text-gray-200">
                {h.name}
                <span className="block text-[10px] text-gray-400">{bandLabel(h.liq)}</span>
              </td>
              <td className="px-2 py-2 text-right tabular-nums text-gray-500">{fmtM(h.invested)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{fmtM(h.value)}</td>
              <td className={`px-2 py-2 text-right tabular-nums ${signClass(h.pnl)}`}>
                {fmtSignedM(h.pnl)}
                <span className="ml-1.5 text-[11px]">{fmtSignedPct(h.pnlPct)}</span>
              </td>
              <td className="px-2 py-2 text-right tabular-nums text-gray-500">{fmtPct(h.weight)}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="px-2 py-2.5">Total</td>
            <td className="px-2 py-2.5 text-right tabular-nums">{fmtM(invested)}</td>
            <td className="px-2 py-2.5 text-right tabular-nums">{fmtM(value)}</td>
            <td className={`px-2 py-2.5 text-right tabular-nums ${signClass(pnl)}`}>
              {fmtSignedM(pnl)}
              <span className="ml-1.5 text-[11px]">{fmtSignedPct(pnlPct)}</span>
            </td>
            <td className="px-2 py-2.5 text-right tabular-nums">100.0%</td>
          </tr>
        </tbody>
      </table>

      <p className="mt-3 text-[10.5px] leading-relaxed text-gray-400">
        Weight is of this portfolio, not of the whole book. Gain or loss is against cost. The
        second line under each holding is how long it takes to turn into cash — the same
        classification the Liquidity tab ladders.
      </p>
    </>
  );
}

const Cell = ({ label, value, sign, sub }) => (
  <div className="min-w-0">
    <div className="text-[9px] font-medium uppercase tracking-[0.12em] text-gray-400">{label}</div>
    <div className={`mt-0.5 text-[12.5px] tabular-nums ${
      sign != null ? signClass(sign) : 'text-gray-800 dark:text-gray-200'}`}>
      {value}
    </div>
    {sub && <div className={`text-[10px] leading-tight ${sign != null ? signClass(sign) : 'text-gray-400'}`}>{sub}</div>}
  </div>
);

// --- overview ---------------------------------------------------------------------------

function Overview({ onBook }) {
  return (
    <div className="flex flex-col gap-4">
      {/* THREE BLOCKS, AND NOTHING ELSE: what am I worth, how did each book do against what
          it is measured on, what can I get to in cash. Everything that was ever a fourth
          block has been removed rather than collapsed — sub-portfolio detail to the Books
          tab, limit headroom to Compliance, which is its whole job. A dashboard is read, not
          studied; anything that has to be explained in a sentence beneath it is either the
          wrong figure or belongs on the tab that owns it. */}
      <Wealth />
      <Performance onBook={onBook} />
      <Reach />
    </div>
  );
}

/** How each book did against what it is measured on — the second thing on the page.
 *
 * IT WAS AT THE BOTTOM, INSIDE A COLLAPSED DISCLOSURE, and it is the most important
 * comparison on the dashboard; on a phone that put it behind a tap while less important
 * blocks sat above it. It now follows the value bridge directly.
 *
 * PER BOOK, NOT PER SUB-PORTFOLIO. Three rows is a comparison a reader can hold in his head;
 * eleven is a table he has to work through, and it is a Books-tab question — tapping a row
 * here opens that book's portfolios, each with the same three figures.
 *
 * THIS BLOCK ALSO CARRIES THE ALLOCATION that used to sit in a separate "Where it sits"
 * panel directly above it. Two consecutive three-row blocks, both keyed by book and both
 * repeating the name and the AUM, is the wall of near-identical cards this redesign exists
 * to remove — so the weight bar and the share of the book ride along here, and nothing is
 * lost. Split them again only if the two questions genuinely need separating.
 */
function Performance({ onBook }) {
  return (
    <Panel title="Returns and benchmark" right="by book, year to date">
      {BOOKS.map((b) => {
        const t = bookTotals(b);
        return (
          <TapRow key={b.key} label={`${b.name} — show its portfolios`}
                  onClick={() => onBook(b.key)}
                  className="border-t border-gray-100 first:border-t-0 dark:border-gray-800/60">
            <span className="flex items-baseline justify-between gap-3">
              <span className={`min-w-0 truncate text-[13px] font-medium ${TONE[b.tone].text}`}>
                {b.name}
              </span>
              <span className="shrink-0 font-serif text-[17px] tabular-nums text-gray-900 dark:text-gray-100">
                {fmtM(t.aum)}
              </span>
            </span>
            <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <span className="block h-full rounded-full"
                    style={{ width: `${Math.max(1, weightOf(t.aum))}%`, background: TONE[b.tone].bar }} />
            </span>
            <span className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 text-[10.5px] text-gray-400">
              <span className="tabular-nums">{pctOfBook(t.aum)} of the book</span>
              <span className={`tabular-nums ${signClass(t.pnl)}`}>{fmtSignedM(t.pnl)} P&L</span>
            </span>
            {/* Nothing at all for the run-off book: it is measured on realisation, so three
                em-dashes in a row would be a group that looks broken rather than one that is
                absent. The basis note below carries the fact instead, and the row simply
                ends after it. The Liquidity Pool DOES render the group — it has a return, so
                its dashes against benchmark and alpha are doing real work. */}
            {hasPerformance(t) && (
              <PerfTriple ret={t.ret} bmk={t.bmk} alpha={t.alpha} className="mt-2" />
            )}
            {!b.benchmarked && (
              <span className="mt-1.5 block text-[10px] leading-tight text-gray-400">{b.basis}</span>
            )}
          </TapRow>
        );
      })}
    </Panel>
  );
}

/** What am I worth, what changed, and WHY it changed.
 *
 * THE CHART IS NAMED AND THE CAUSES ARE SEPARATED. Before, the line ran from opening value to
 * closing value and rose by exactly the year's P&L — which silently asserted that no money
 * was paid in or taken out, an assertion nobody made and no reader could detect. The line
 * plots PORTFOLIO VALUE and says so on itself, because that is the same quantity as the
 * headline above it; the bridge underneath then splits the move into performance and flows,
 * so a reader can see why the value went where it went rather than only that it did.
 *
 * The two change figures are labelled "performance", not "this year" and "this month". With
 * flows in the period those words are ambiguous — the value moved by a different amount than
 * the portfolio earned, and in September by more than a third different.
 */
function Wealth() {
  const first = AUM_TRAIL[0];
  const last = AUM_TRAIL[AUM_TRAIL.length - 1];

  return (
    <section className="rounded-xl border border-gray-200 p-4 sm:p-5 dark:border-gray-800">
      <Headline label="Total portfolio value" value={fmtM(TOTAL_AUM)} unit="AED m">
        <div className="mt-1.5 text-[11px] text-gray-400">across three books</div>
      </Headline>

      <div className="mt-3">
        {/* The line is named because it is NOT the performance figure beside it — it moves
            with cash in and out too, and with the bridge closed by default this caption is
            the only thing on screen saying so. It is the one caption on this block that is
            not restating an adjacent number, which is why it survived the trim. */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-gray-400">
            Portfolio value
          </span>
          <span className="text-[10px] text-gray-400">includes cash inflow / outflow</span>
        </div>
        <Sparkline points={AUM_TRAIL} />
        {/* Opening on the left because it is the one endpoint not already on screen; the
            right end is the headline figure two lines above, so it gets a month and no
            number. */}
        <div className="flex items-baseline justify-between text-[10px] tabular-nums text-gray-400">
          <span>{first.month} {fmtM(first.aum)}</span>
          <span>{last.month}</span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-4 border-t border-gray-100 pt-3 dark:border-gray-800/60">
        <Figure label="Performance this year" value={`${fmtSignedM(TOTAL_PNL)} m`} sign={TOTAL_PNL}
                sub={fmtSignedPct(YTD_ON_OPENING)} />
        <Figure label={`Performance in ${MONTH_LABEL}`} value={`${fmtSignedM(MONTH_PNL)} m`}
                sign={MONTH_PNL} sub={fmtSignedPct(MONTH_PCT)} />
      </div>

      {/* CLOSED ON LOAD AT EVERY WIDTH. The two figures above answer "what changed"; the
          split into performance and cash flows is the follow-up question, and only for a
          reader who asks it. */}
      <div className="mt-2 border-t border-gray-100 pt-1.5 dark:border-gray-800/60">
        <Collapse label="Why the value moved"><Bridge /></Collapse>
      </div>
    </section>
  );
}

/** Opening → performance → cash inflow / outflow → closing.
 *
 * A VERTICAL LIST OF ROWS, NOT A WATERFALL. Four floating columns with connecting steps is
 * the conventional drawing and it does not survive 375px: the step labels collide, the
 * smallest bar (net flows, 95 against a 12,164 opening) is under two pixels tall, and the
 * figures end up smaller than the body text. Rows carry the same four numbers at full size
 * and read in the direction the eye already travels. The arithmetic is the point here, not
 * the picture.
 *
 * FLOWS ARE NOT COLOURED GREEN OR RED, and that is a deliberate departure from the
 * green-up/red-down rule this portal applies to every other signed number. A withdrawal is
 * not a loss — the owner taking his own money out is neither good nor bad news — and
 * painting it red would re-create, in colour, exactly the performance/flow conflation this
 * block exists to undo. The sign and the words "paid in" and "taken out" carry the direction
 * instead. Performance keeps its colour, because a gain or a loss genuinely is one.
 *
 * NO ROW NOTES. Each row used to carry a second line under its label — "start of the year",
 * "420 paid in · 325 taken out", the return restated as a percentage. Every one of them said
 * what the label or the figure beside it already said. A dashboard is read, not studied.
 */
function Bridge() {
  const rows = [
    { label: 'Opening', value: fmtM(OPENING_AUM) },
    { label: 'Performance', value: fmtSignedM(TOTAL_PNL), sign: TOTAL_PNL },
    { label: 'Cash inflow / outflow', value: fmtSignedM(NET_FLOWS) },
    { label: 'Portfolio value now', value: fmtM(TOTAL_AUM), strong: true },
  ];

  return (
    <div>
      <div>
        {rows.map((r) => (
          <div key={r.label}
               className={`flex items-baseline justify-between gap-3 py-1.5 ${r.strong
                 ? 'mt-0.5 border-t border-gray-200 pt-2 font-semibold dark:border-gray-700' : ''}`}>
            <span className="min-w-0 text-[12px] text-gray-700 dark:text-gray-300">{r.label}</span>
            <span className={`shrink-0 text-[13px] tabular-nums ${
              r.sign != null ? signClass(r.sign) : 'text-gray-900 dark:text-gray-100'}`}>
              {r.value}
            </span>
          </div>
        ))}
      </div>
      {/* Trivially true while opening value is derived from the other three. It stops being
          trivial the day a real opening balance is fed in, which is when a reader most needs
          to be told the four figures no longer agree. */}
      {!BRIDGE_RECONCILES && (
        <p className="mt-2 rounded-lg bg-amber-100 px-3 py-2 text-[11px] font-semibold leading-relaxed text-amber-900 dark:bg-amber-950/70 dark:text-amber-200">
          These figures do not reconcile: opening value plus performance plus net flows comes
          to {fmtM(OPENING_AUM + TOTAL_PNL + NET_FLOWS)} against a portfolio value
          of {fmtM(TOTAL_AUM)}.
        </p>
      )}
    </div>
  );
}

/** What can be turned into cash, and how fast.
 *
 * THREE PLAIN FIGURES, NO PROSE. This block carried a headline of "available without notice"
 * over the two speed figures, plus a paragraph distinguishing what can be realised from what
 * may be distributed. Both are gone: "same day" and "within a week" say what they are, and
 * the paragraph read as a lecture. The distinction it drew is real, so if the two figures are
 * ever made to agree with each other, or Distributable is redefined as a ladder band, that is
 * the moment to look at it again — the ladder itself is on the Liquidity tab.
 *
 * Three across even at 375px: they are short numbers under short labels, and stacking them
 * would make a three-line block out of a one-line answer.
 */
function Reach() {
  return (
    <Panel title="Liquidity available" right="AED m">
      <div className="grid grid-cols-3 gap-3">
        <Figure label="Same day" value={fmtM(SAME_DAY)} sub={pctOfBook(SAME_DAY)} />
        <Figure label="Within a week" value={fmtM(WITHIN_WEEK)} sub={pctOfBook(WITHIN_WEEK)} />
        <Figure label="Distributable" value={fmtM(DISTRIBUTABLE)} sub={pctOfBook(DISTRIBUTABLE)} />
      </div>
    </Panel>
  );
}

// --- books -------------------------------------------------------------------------------

/** THE BOOKS TAB OWNS THE SUB-PORTFOLIO DETAIL.
 *
 * The eleven portfolios and their returns, benchmarks and alphas were removed from the
 * Overview, so this is now the only place they are shown and it has to carry all of it —
 * per line: AUM, share of the book, holdings count, P&L, return, benchmark and alpha. Nothing
 * was dropped in that move, only relocated; if you remove a figure from here, put it back on
 * the Overview first.
 *
 * The paired-bar chart of a benchmarked book's portfolios against their benchmarks lives
 * here too, behind a disclosure on a phone — it is a comparison worth having but it is five
 * rows of bars, which is not what a reader should meet before the figures themselves.
 */
function Books({ onLine }) {
  return (
    <div className="flex flex-col gap-4">
      {BOOKS.map((b) => {
        const t = bookTotals(b);
        const maxRet = Math.max(...b.lines.flatMap((l) => [l.ret || 0, l.bmk || 0]), 0.01);
        return (
          <Panel key={b.key} title={b.name} right={b.note} className={`border-l-[3px] ${TONE[b.tone].edge}`}>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              <Kpi label="AUM" value={`AED ${fmtM(t.aum)} m`} tone={b.tone} />
              <Kpi label="Weight" value={fmtPct(weightOf(t.aum))} tone={b.tone} />
              <Kpi label="P&L" value={fmtSignedM(t.pnl)} sign={t.pnl} />
              {/* The run-off book has neither a return nor an alpha: it is measured on
                  realisation. The tile shows what it does have rather than a dash, so the
                  grid never presents an empty slot as a missing reading. */}
              {b.benchmarked
                ? <Kpi label="Alpha" value={`${fmtSigned(t.alpha)} pp`} sign={t.alpha} tone={b.tone} />
                : t.ret != null
                  ? <Kpi label="Return" value={fmtPct(t.ret)} tone={b.tone} />
                  : <Kpi label="Invested" value={`AED ${fmtM(t.invested)} m`} tone={b.tone} />}
            </div>

            {/* The book's measurement basis, said once at the top rather than repeated on
                every line. The per-line dashes below show WHERE a figure is absent; this
                says WHY, which is a fact about the book and belongs to the book. */}
            <p className="mt-3 text-[10.5px] leading-relaxed text-gray-400">{b.basis}</p>

            <div className="mt-2">
              {b.lines.map((l) => (
                <TapRow key={l.name} label={`${l.name} — show holdings`}
                        onClick={() => onLine(b.key, l.name)}
                        className="border-t border-gray-100 first:border-t-0 dark:border-gray-800/60">
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-[13px] text-gray-700 dark:text-gray-300">{l.name}</span>
                    <span className={`shrink-0 text-[13px] font-semibold tabular-nums ${TONE[b.tone].text}`}>
                      {fmtM(l.aum)}
                    </span>
                  </span>
                  <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                    <span className="block h-full rounded-full"
                          style={{ width: `${Math.max(1, (l.aum / t.aum) * 100)}%`,
                                   background: TONE[b.tone].bar }} />
                  </span>
                  <span className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 text-[10.5px] text-gray-400">
                    <span className="tabular-nums">
                      {fmtPct((l.aum / t.aum) * 100)} of the book · {l.holdings.length} holdings
                    </span>
                    <span className={`tabular-nums ${signClass(l.pnl)}`}>{fmtSignedM(l.pnl)}</span>
                  </span>
                  <PerfTriple ret={l.ret} bmk={l.bmk} alpha={lineAlpha(l)} className="mt-2" />
                </TapRow>
              ))}
            </div>

            {/* Only drawn for a book that HAS a benchmark. Rendering an empty chart, or one
                with the benchmark bars at zero, would assert a benchmark of nil. */}
            {b.benchmarked && (
              <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-800/60">
                <Disclosure label="Compare with the benchmark">
                  <PairedBars max={maxRet}
                              rows={b.lines.map((l) => ({ name: l.name, a: l.ret, b: l.bmk }))} />
                </Disclosure>
              </div>
            )}
          </Panel>
        );
      })}
      <p className="text-[11px] leading-relaxed text-gray-400">
        Tap any portfolio for its holdings — what was invested, what it is worth now, and the
        gain or loss on each. A dash means the figure does not exist for that book, not that
        it is zero.
      </p>
    </div>
  );
}

// --- liquidity ----------------------------------------------------------------------------

/** How liquid the WHOLE asset base is.
 *
 * The ladder used to cover the Liquidity Pool alone and summed to a quarter of the book while
 * reading like the whole picture. It now partitions every holding in all three books, so the
 * long private assets appear where they belong — at the far end — instead of being left off a
 * page titled "liquidity".
 *
 * CUMULATIVE IS THE PRIMARY FIGURE, per band second. The question is "how much can I get to,
 * and how fast", and a per-band table makes the reader add four rows in his head to answer
 * it. The bars therefore grow to 100% down the page: the shape itself is the answer.
 */
function Liquidity() {
  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-xl border border-gray-200 p-4 sm:p-5 dark:border-gray-800">
        <Headline label="Realisable within a week" value={fmtM(WITHIN_WEEK)} unit="AED m">
          <div className="mt-1.5 text-[11px] text-gray-400">
            {pctOfBook(WITHIN_WEEK)} of the whole {fmtM(TOTAL_AUM)} m asset base
          </div>
        </Headline>
        <div className="mt-3 grid grid-cols-3 gap-3 border-t border-gray-100 pt-3 dark:border-gray-800/60">
          <Figure label="Same day" value={fmtM(SAME_DAY)} sub={pctOfBook(SAME_DAY)} />
          <Figure label="Within a year" value={fmtM(WITHIN_YEAR)} sub={pctOfBook(WITHIN_YEAR)} />
          <Figure label="Beyond 3 years" value={fmtM(BEYOND_THREE_YEARS)} sub={pctOfBook(BEYOND_THREE_YEARS)} />
        </div>
      </section>

      <Panel title="Time to cash" right="cumulative, AED m">
        {LIQUIDITY_LADDER.map((r) => (
          <div key={r.key} className="border-t border-gray-100 py-3 first:border-t-0 first:pt-0 dark:border-gray-800/60">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 text-[13px] text-gray-800 dark:text-gray-200">{r.by}</span>
              <span className="shrink-0 font-serif text-[17px] tabular-nums text-gray-900 dark:text-gray-100">
                {fmtM(r.cumulative)}
              </span>
            </div>
            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div className="h-full rounded-full transition-[width] duration-500"
                   style={{ width: `${Math.max(1, r.cumulativePct)}%`, background: TONE.teal.bar }} />
            </div>
            <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-[10.5px] text-gray-400">
              <span className="tabular-nums">{fmtPct(r.cumulativePct)} of the book</span>
              <span className="tabular-nums">{r.band}: +{fmtM(r.amount)}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[10px] text-gray-400">
              <span className="italic">{r.note}</span>
              {r.byBook.map((x) => (
                <span key={x.key} className={`tabular-nums ${TONE[x.tone].text}`}>
                  {x.name} {fmtM(x.amount)}
                </span>
              ))}
            </div>
          </div>
        ))}

        {/* The reconciliation is stated, not implied. Every holding sits in exactly one band,
            so the bands sum to the book by construction — but a reader should be able to
            check that rather than trust it, and a future edit that breaks it says so here
            instead of quietly showing a book that does not add up. */}
        <div className={`mt-3 rounded-lg px-3 py-2.5 text-[11px] leading-relaxed ${LADDER_RECONCILES
          ? 'bg-gray-50 text-gray-500 dark:bg-gray-800/40 dark:text-gray-400'
          : 'bg-amber-100 font-semibold text-amber-900 dark:bg-amber-950/70 dark:text-amber-200'}`}>
          {LADDER_RECONCILES ? (
            <>
              The ladder covers the entire asset base: every holding in all three books sits in
              exactly one band, and the bands total{' '}
              <span className="font-semibold tabular-nums">AED {fmtM(TOTAL_AUM)} m</span> — the
              same figure as total portfolio value.
            </>
          ) : (
            <>
              This ladder does not reconcile: AED {fmtM(LADDER_UNCLASSIFIED)} m of holdings
              carry no time-to-cash classification and are missing from every band. The bands
              total {fmtM(LADDER_BANDED)} m against a book of {fmtM(TOTAL_AUM)} m.
            </>
          )}
        </div>
      </Panel>

      <Panel title="Distributable liquidity" right="policy, not settlement">
        <Headline label="Available without notice" value={fmtM(DISTRIBUTABLE)} unit="AED m" />
        <p className="mt-2.5 text-[11px] leading-relaxed text-gray-400">
          This is a policy figure and is deliberately NOT a band of the ladder above. The
          ladder says how fast an asset turns into cash; this says how much the framework
          permits to be released inside its notice period, which is less — some of what could
          be sold is committed, pledged, or held against the liquidity floor.
        </p>
      </Panel>
    </div>
  );
}

// --- run-off -------------------------------------------------------------------------------

function Runoff({ onLine }) {
  const t = bookTotals(runoff);
  const maxExp = Math.max(...RUNOFF_SCHEDULE.map((r) => r.expected));
  return (
    <div className="flex flex-col gap-4">
      <KpiGrid>
        <Kpi label="Book value" value={`AED ${fmtM(t.aum)} m`} tone="gold" />
        <Kpi label="Weight" value={fmtPct(weightOf(t.aum))} tone="gold" />
        <Kpi label="Realised to date" value={`AED ${fmtM(RUNOFF_REALISED)} m`} sign={RUNOFF_REALISED} />
        <Kpi label="Cash generated" value={`AED ${fmtSignedM(t.pnl)} m`} sign={t.pnl} />
      </KpiGrid>

      <Panel title="Expected realisation" right="AED m, by period">
        {RUNOFF_SCHEDULE.map((r) => (
          <div key={r.period} className="border-t border-gray-100 py-2.5 first:border-t-0 dark:border-gray-800/60">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13px] text-gray-800 dark:text-gray-200">{r.period}</span>
              <span className="shrink-0 text-[12.5px] tabular-nums">
                {r.realised != null && (
                  <span className={`mr-2 font-semibold ${signClass(r.realised)}`}>
                    {fmtM(r.realised)} realised
                  </span>
                )}
                <span className="text-gray-500 dark:text-gray-400">{fmtM(r.expected)} expected</span>
              </span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div className="h-full rounded-full"
                   style={{ width: `${(r.expected / maxExp) * 100}%`, background: TONE.gold.bar }} />
            </div>
          </div>
        ))}
        <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-gray-200 pt-2.5 text-[12px] font-semibold dark:border-gray-700">
          <span>Expected in total</span>
          <span className="tabular-nums">{fmtM(RUNOFF_EXPECTED)}</span>
        </div>
        <p className="mt-1.5 text-[10.5px] leading-relaxed text-gray-400">
          {RUNOFF_EXPECTED === t.aum
            ? `Which is the book's whole carrying value of ${fmtM(t.aum)} — the schedule realises all of it.`
            : `Against a carrying value of ${fmtM(t.aum)}, a difference of ${fmtSignedM(RUNOFF_EXPECTED - t.aum)}.`}
        </p>
      </Panel>

      <Panel title="What is being run off" right="tap for holdings">
        {runoff.lines.map((l) => (
          <TapRow key={l.name} label={`${l.name} — show holdings`}
                  onClick={() => onLine(runoff.key, l.name)}
                  className="border-t border-gray-100 first:border-t-0 dark:border-gray-800/60">
            <span className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-[13px] text-gray-800 dark:text-gray-200">{l.name}</span>
              <span className="shrink-0 text-[13px] font-semibold tabular-nums text-amber-700 dark:text-amber-500">
                {fmtM(l.aum)}
              </span>
            </span>
            <span className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 text-[10.5px] text-gray-400">
              <span className="tabular-nums">{l.holdings.length} holdings</span>
              <span className={`tabular-nums ${signClass(l.pnl)}`}>{fmtSignedM(l.pnl)} against cost</span>
            </span>
          </TapRow>
        ))}
      </Panel>

      <p className="text-[11px] leading-relaxed text-gray-400">
        This book is measured on realisation and cash generated, not on return, so no
        benchmark or alpha is shown for it.
      </p>
    </div>
  );
}

// --- compliance ------------------------------------------------------------------------
//
// NOT "Policies". The Policy Pack is a SEPARATE AREA of this portal, at /policies, holding
// the framework itself with its drafts and approvals. A dashboard tab called Policies reads
// as a link to that area and is not one — this tab shows exposures measured against limits,
// which is a compliance reading, not the policy documents. The pointer at the foot is kept,
// and is now unambiguously a cross-reference rather than a second thing with the same name.

function Compliance() {
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Exposure against limits" right="current reading">
        {POLICY_LIMITS.map((p) => {
          // An unset limit is NOT a limit of zero and must not be drawn as a breach. The
          // liquidity floor is genuinely undecided in the framework, so it shows the actual
          // and says the limit is not set — the same position the Delta module takes. The
          // arithmetic lives in limitStatus so this tab and the Overview's risk block cannot
          // disagree about what is tight.
          const { unset, used, near, breach } = limitStatus(p);
          return (
            <div key={p.name} className="border-t border-gray-100 py-2.5 first:border-t-0 dark:border-gray-800/60">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-[13px] text-gray-800 dark:text-gray-200">{p.name}</span>
                <span className="shrink-0 text-[12.5px] tabular-nums">
                  <span className={breach ? 'font-semibold text-red-700 dark:text-red-400'
                    : near ? 'font-semibold text-amber-700 dark:text-amber-500' : ''}>
                    {p.actual.toFixed(1)}
                  </span>
                  <span className="text-gray-400">
                    {unset ? ' — no limit set' : ` / ${p.limit} ${p.unit}`}
                  </span>
                </span>
              </div>
              {!unset && (
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  <div className="h-full rounded-full"
                       style={{ width: `${Math.max(1, Math.min(100, used))}%`,
                                background: breach ? '#b91c1c' : near ? TONE.gold.bar : TONE.navy.bar }} />
                </div>
              )}
            </div>
          );
        })}
      </Panel>

      <p className="text-[11px] leading-relaxed text-gray-400">
        These are readings against the limits, not the limits themselves. The framework —
        the policies, their drafts and their approvals — is a separate area of this portal:
        the Policy Pack.
      </p>
    </div>
  );
}
